/**
 * @file Sandbox input handler.
 *
 * Handles raycasting from mouse clicks on the canvas to place/delete enemies
 * in the 3D world. Uses Three.js Raycaster to project clicks onto the
 * terrain plane.
 */

import * as THREE from 'three';
import { Transform } from '../components/Transform.js';
import { PlayerControlled } from '../components/PlayerControlled.js';
import { createEnemy } from '../entities/Enemy.js';
import { sandboxState } from './SandboxConfig.js';

/**
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {HTMLCanvasElement} canvas Render canvas.
 * @returns {{ register: () => void, unregister: () => void, clearAll: () => void, getCount: () => number, trackEntity: (e: import('../ecs/Entity.js').Entity) => void }}
 */
export function createSandboxInput(ctx, canvas) {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const intersection = new THREE.Vector3();
  const worldPos = new THREE.Vector3();
  const screenPos = new THREE.Vector3();

  /** @type {Map<number, import('../ecs/Entity.js').Entity>} Sandbox-created entities (for deletion). */
  const sandboxEntities = new Map();

  /**
   * Get the click position on the ground plane.
   * @param {PointerEvent} event
   * @returns {{ x: number, z: number } | null}
   */
  function getGroundPosition(event) {
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycaster.setFromCamera(mouse, ctx.cameraController.camera);
    // intersectPlane returns null when the ray is parallel / misses; the target
    // vector is still the previous contents in that case.
    const hit = raycaster.ray.intersectPlane(plane, intersection);
    if (hit == null) return null;
    return { x: intersection.x, z: intersection.z };
  }

  /**
   * Check if a position is valid for spawning (not too close to player).
   * @param {number} x
   * @param {number} z
   * @returns {boolean}
   */
  function isValidSpawn(x, z) {
    const playerEntities = ctx.world.query(PlayerControlled, Transform);
    if (playerEntities.length === 0) return true;

    const player = playerEntities[0];
    const t = player.get(Transform);
    if (!t) return true;

    const dx = x - t.position.x;
    const dz = z - t.position.z;
    return (dx * dx + dz * dz) > 64; // at least 8 units away
  }

  /**
   * Spawn a single enemy at the given position.
   * @param {number} x
   * @param {number} z
   * @param {string} type
   * @returns {import('../ecs/Entity.js').Entity | null}
   */
  function spawnEnemy(x, z, type) {
    if (sandboxState.enemyToggles[type] === false) return null;
    try {
      const entity = createEnemy(ctx, x, z, type);
      sandboxEntities.set(entity.id, entity);
      return entity;
    } catch (e) {
      console.warn('[Sandbox] Failed to spawn enemy:', e);
      return null;
    }
  }

  /**
   * Handle a click on the canvas for sandbox placement/deletion.
   * @param {PointerEvent} event
   * @param {boolean} isRightClick
   */
  function onCanvasClick(event, isRightClick) {
    if (!ctx || !ctx.arena) return;

    if (isRightClick) {
      // Right-click deletes the nearest sandbox-spawned enemy under the cursor.
      const rect = canvas.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, ctx.cameraController.camera);

      let nearest = null;
      let nearestDist = Infinity;

      for (const entity of sandboxEntities.values()) {
        if (!entity.alive) {
          sandboxEntities.delete(entity.id);
          continue;
        }
        const t = entity.get(Transform);
        if (!t) continue;

        worldPos.set(t.position.x, 0, t.position.z);
        screenPos.copy(worldPos).project(ctx.cameraController.camera);

        const dx = screenPos.x - mouse.x;
        const dy = screenPos.y - mouse.y;
        const dist = dx * dx + dy * dy;

        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = entity;
        }
      }

      if (nearest && nearestDist < 0.25) {
        ctx.world.destroyEntity(nearest);
        sandboxEntities.delete(nearest.id);
      }
      return;
    }

    // Left click: placement (spray mode)
    if (!sandboxState.sprayMode) return;

    const pos = getGroundPosition(event);
    if (!pos || !isValidSpawn(pos.x, pos.z)) return;

    // Spray: place multiple enemies in a cluster
    const count = Math.max(1, sandboxState.sprayCount | 0);
    const type = sandboxState.sprayType;
    const radius = count > 1 ? 2.0 : 0;

    for (let i = 0; i < count; i++) {
      const angle = count > 1 ? (Math.PI * 2 * i) / count + (Math.random() * 0.5 - 0.25) : 0;
      const dist = count > 1 ? Math.random() * radius : 0;
      const sx = pos.x + Math.cos(angle) * dist;
      const sz = pos.z + Math.sin(angle) * dist;

      if (isValidSpawn(sx, sz)) {
        spawnEnemy(sx, sz, type);
      }
    }
  }

  /** @param {MouseEvent} e */
  function onClick(e) {
    onCanvasClick(e, false);
  }

  /** @param {MouseEvent} e */
  function onContextMenu(e) {
    e.preventDefault();
    onCanvasClick(e, true);
  }

  /**
   * Register the canvas click listeners.
   */
  function register() {
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('contextmenu', onContextMenu);
  }

  /**
   * Remove listeners.
   */
  function unregister() {
    canvas.removeEventListener('click', onClick);
    canvas.removeEventListener('contextmenu', onContextMenu);
  }

  /**
   * Get the count of sandbox entities.
   * @returns {number}
   */
  function getCount() {
    return sandboxEntities.size;
  }

  /**
   * Clear all sandbox entities.
   * @returns {void}
   */
  function clearAll() {
    if (!ctx || !ctx.world) return;
    for (const entity of sandboxEntities.values()) {
      if (entity.alive) ctx.world.destroyEntity(entity);
    }
    sandboxEntities.clear();
  }

  /**
   * Register a sandbox entity for tracking.
   * @param {import('../ecs/Entity.js').Entity} entity
   */
  function trackEntity(entity) {
    sandboxEntities.set(entity.id, entity);
  }

  return {
    register,
    unregister,
    onCanvasClick,
    getCount,
    clearAll,
    trackEntity
  };
}
